import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { api } from "@/api/apiSlice";
import { backendBase } from "@/api/backendBase";
import { OrderTags } from "./OrderTags";
import type { OrderListResponseDto } from "@/api/dtos";
import type { Order } from "./_models/Order";
import { toOrderFromOrderDto } from "./_transformers/toOrderFromOrderDto";

/** GET /orders' filters; empty / absent = no filter. */
export interface GetOrderListArgs {
  /** Only orders whose retailer order TOE has reported on (the ones with something to reconcile). */
  crossSystemOnly?: boolean;
  search?: string;
  sources?: string[];
  statuses?: string[];
  customerIds?: string[];
  /** yyyy-MM-dd, inclusive (Sydney business days). */
  orderDateFrom?: string;
  orderDateTo?: string;
}

/** The GET /orders query string, newest-imported first; lists are comma-joined (the API doesn't read `status[]=`). */
export const toOrderListParams = (args: GetOrderListArgs | void): Record<string, string> => {
  const params: Record<string, string> = {
    orderBy: "createdAt",
    orderDirection: "desc",
  };
  if (!args) {
    return params;
  }
  if (args.crossSystemOnly) params.withParentSystem = "TOE";
  const search = (args.search ?? "").trim();
  if (search) params.search = search;
  if (args.sources && args.sources.length > 0) params.source = args.sources.join(",");
  if (args.statuses && args.statuses.length > 0) params.status = args.statuses.join(",");
  if (args.customerIds && args.customerIds.length > 0) params.customerId = args.customerIds.join(",");
  if (args.orderDateFrom) params.orderDateFrom = args.orderDateFrom;
  if (args.orderDateTo) params.orderDateTo = args.orderDateTo;
  return params;
};
/** Orders from our Postgres store (GET /orders), one page at the backend's default limit, each with its lines and every system's statuses. */
export const getOrderListApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getOrderList: builder.query<Order[], GetOrderListArgs | void>({
      queryFn: async (args) => {
        try {
          const params = toOrderListParams(args);
          const res = await axios.get<OrderListResponseDto>(
            `${backendBase()}/orders`,
            { params, headers: { Accept: "application/json" } },
          );
          const orders = res.data.orders.map((x) => toOrderFromOrderDto(x));
          return { data: orders };
        }
        catch (e) {
          if (axios.isAxiosError(e) && e.response) {
            return {
              error: { status: e.response.status, data: e.response.data } as FetchBaseQueryError,
            };
          }
          return {
            error: { status: "FETCH_ERROR", error: String(e) } as FetchBaseQueryError,
          };
        }
      },
      providesTags: [{ type: OrderTags.Order, id: "LIST" }],
    }),
  }),
});

export const getOrderList = getOrderListApi.endpoints.getOrderList;
